import type AHDate from "_shared/dates/date";
import type { PermissionKind } from "permissions/_api/permission-kinds-api-service";
import { AccessLevel, Scope } from "permissions/resources/permission-resource-types";
import { WorkAgreementField } from "permissions/resources/work/work-agreement-field";

/**
 * Every word the permissions pages use for a grant, defined once.
 *
 * The kind names and "may …" sentences come from `GET /permissions/kinds`, so the page, the add
 * dialog and the assistant speak the same vocabulary. This module turns that vocabulary and a
 * grant's level, scope, source and state into the strings a row shows. It never prints an enum name
 * ("Read/Write", "Entity-Wide") and never prints "—": an empty cell shows nothing, a zero count
 * "None". The table of these strings is in `docs/design/permissions/PERMISSIONS_UI_DESIGN.md`, under
 * "Wording".
 */

/** The role a member holds in the organization, as the roster and the person page print it. */
export const MEMBER_ROLE = "Member";

/**
 * The role of a member who holds signing authority. It is the kind's own name, so the roster's role
 * column, the person page's role chip and the signing authority row all say the same thing. The
 * sketch said "Signer"; one word for one grant beats a shorter one.
 */
export const SIGNING_AUTHORITY_ROLE = "Signing authority";

export function memberRole(hasSigningAuthority: boolean | undefined): string {
  return hasSigningAuthority ? SIGNING_AUTHORITY_ROLE : MEMBER_ROLE;
}

/** The kind's name as a row header: the server's lower-case name with its first letter raised. */
export function kindLabel(kind: Pick<PermissionKind, "name">): string {
  return kind.name.charAt(0).toUpperCase() + kind.name.slice(1);
}

/** What the kind lets its holder do, as the second line under the kind. */
export function kindAllows(kind: Pick<PermissionKind, "allows">): string {
  return `may ${kind.allows}`;
}

/** The two levels by name, for a filter or the field editor, where a level stands on its own. */
export const ACCESS_LEVEL_NAMES: Readonly<Record<AccessLevel, string>> = {
  [AccessLevel["Read-Only"]]: "Read only",
  [AccessLevel["Read/Write"]]: "Read and write",
};

/** The two levels as the start of the level chip: "Can edit · whole organization". */
export const ACCESS_LEVEL_VERBS: Readonly<Record<AccessLevel, string>> = {
  [AccessLevel["Read-Only"]]: "Can see",
  [AccessLevel["Read/Write"]]: "Can edit",
};

export const SCOPE_NAMES: Readonly<Record<Scope, string>> = {
  [Scope["Need-to-Know"]]: "Need-to-know",
  [Scope["Entity-Wide"]]: "Whole organization",
  [Scope.Global]: "Global (super-user)",
};

/** What a scope means, shown under each option in the level control rather than in a native title. */
export const SCOPE_HELP: Readonly<Record<Scope, string>> = {
  [Scope["Need-to-Know"]]: "Only the records they are already part of",
  [Scope["Entity-Wide"]]: "Every record of this kind in the organization",
  [Scope.Global]: "Every record of this kind on the platform",
};

/** The scope as it reads after the level verb, in the middle of a sentence. */
const SCOPE_IN_A_SENTENCE: Readonly<Record<Scope, string>> = {
  [Scope["Need-to-Know"]]: "need-to-know",
  [Scope["Entity-Wide"]]: "whole organization",
  [Scope.Global]: "global",
};

/** What the level cell says for a kind with no level or scope to choose, such as signing authority. */
export const HELD = "Allowed";

export const NOT_GRANTED = "Not granted";

export type GrantLevel = Readonly<{ accessLevel?: AccessLevel; scope?: Scope }>;

/**
 * The level chip's text. "Can edit · whole organization" for a kind with both controls, the scope
 * alone for document contents, the level alone for a per-record kind, and "Allowed" for a kind that
 * has neither.
 */
export function levelLabel(level: GrantLevel): string {
  const verb = level.accessLevel === undefined ? undefined : ACCESS_LEVEL_VERBS[level.accessLevel];
  if (level.scope === undefined) return verb ?? HELD;
  if (verb === undefined) return SCOPE_NAMES[level.scope];
  return `${verb} · ${SCOPE_IN_A_SENTENCE[level.scope]}`;
}

/** The second line of a level option: what its scope covers, or nothing for a level-only kind. */
export function levelHelp(level: GrantLevel): string | undefined {
  return level.scope === undefined ? undefined : SCOPE_HELP[level.scope];
}

/** The heading of the level control. */
export const LEVEL_CONTROL_TITLE = "How much access";

/** The records a kind's Need-to-know reach can be counted in, and the count: "4 proposals". */
export type ReachCount = Readonly<{ count: number; noun: string }>;

const GLOBAL_COVERS = "Covers the same across every organization on the platform. Only a platform administrator changes it.";
const GLOBAL_WOULD_COVER = "Global would cover the same across every organization on the platform.";

function reachCount({ count, noun }: ReachCount): string {
  return count === 0 ? `no ${noun}s` : `${count.toLocaleString()} ${noun}${count === 1 ? "" : "s"}`;
}

/**
 * What a standing kind covers for this person, as the lines under its "may …" sentence on the person
 * page (#6831). The clauses are the server's (`reach` on `GET /permissions/kinds`, written from the
 * checks that ask for the kind); this only joins them into sentences.
 *
 * - **Held**: what the held scope covers, "Covers …", with the count of records the person is on at
 *   Need-to-know. "Not in effect: …" where no check accepts the grant, at its scope or its level.
 *   Global covers every organization and only a platform administrator changes it.
 * - **Not held** (or removed): what each scope the kind offers would cover. Global is listed only
 *   where the kind offers it, and to a platform administrator, who alone may grant it.
 * - A kind with no reach, such as the sign-in kinds, has nothing beyond its "may …" sentence.
 */
export function kindReach(
  kind: Pick<PermissionKind, "reach" | "leastEffectiveLevel" | "controls">,
  held: GrantLevel | undefined,
  options: Readonly<{ mayAdministerPlatform: boolean; count?: ReachCount }>,
): string[] {
  if (kind.reach.length === 0) return [];
  if (!held) {
    const lines = kind.reach.map((reach) => `${SCOPE_NAMES[reach.scope as Scope]} would cover ${reach.covers}.`);
    const offersGlobal = kind.controls.some(
      (control) => control.name === "scope" && control.options.some((option) => option.value === Scope.Global),
    );
    return options.mayAdministerPlatform && offersGlobal ? [...lines, GLOBAL_WOULD_COVER] : lines;
  }

  if (held.scope === Scope.Global) return [GLOBAL_COVERS];
  const reach = kind.reach.find((candidate) => candidate.scope === held.scope);
  if (!reach) return [];
  if (!reach.inEffect) return [`Not in effect: at ${SCOPE_IN_A_SENTENCE[reach.scope as Scope]} it covers ${reach.covers}.`];
  const least = kind.leastEffectiveLevel as AccessLevel | null;
  if (least !== null && held.accessLevel !== undefined && held.accessLevel < least) {
    return [
      `Not in effect: it is checked at ${ACCESS_LEVEL_VERBS[least].toLowerCase()}, and this grant is ${ACCESS_LEVEL_VERBS[held.accessLevel].toLowerCase()}.`,
    ];
  }
  const count = options.count && reach.scope === Scope["Need-to-Know"] ? `: ${reachCount(options.count)} now` : "";
  return [`Covers ${reach.covers}${count}.`];
}

/** The day a grant was made or removed: "12 Aug", with the year once it is not this year's. */
export function grantDate(date: AHDate, today: AHDate): string {
  return date.year === today.year ? date.format("D MMM") : date.format("D MMM YYYY");
}

export function grantedBy(grantorName: string, date: AHDate | undefined, today: AHDate): string {
  return date ? `Granted by ${grantorName}, ${grantDate(date, today)}` : `Granted by ${grantorName}`;
}

/**
 * The source of a grant whose grantor cannot be named: "Granted 12 Aug", with the grantor's state
 * beneath it, so the state sentence never stands where the name would.
 */
export function grantedOn(date: AHDate | undefined, today: AHDate): string {
  return date ? `Granted ${grantDate(date, today)}` : "Granted";
}

/**
 * The source of a grant a template application created or raised (#6828): `From template "Paralegal"`.
 * The row links it to the template while the template is live.
 */
export function fromTemplate(templateName: string): string {
  return `From template "${templateName}"`;
}

/** A grant made by onboarding, an invitation, a frozen forum or the document creator rule. */
export const BY_THE_SYSTEM = "By the system";

/** The words before the parents a derived grant came from: "From: proposals (whole organization)". */
export const FROM = "From:";

/** One parent of a derived grant: its kind and, where it has one, its scope. */
export function parentLabel(kind: Pick<PermissionKind, "name">, scope: Scope | undefined): string {
  return scope === undefined ? kind.name : `${kind.name} (${SCOPE_IN_A_SENTENCE[scope]})`;
}

/** A parent the kinds endpoint does not describe. It is still a grant somebody holds. */
export const UNRECOGNIZED_PARENT = "a grant this page does not recognize";

/**
 * What wrote a roster-derived forum grant. The note is "Derived forum access:{source}", where the
 * source is one of the server's `DerivedForumAccessSources`. Naming the proposal or work agreement
 * itself needs the target lookup that per-record rows add (#6170).
 */
const DERIVED_FORUM_SOURCES: Readonly<Record<string, string>> = {
  "Work.Proposal participant association": "taking part in a proposal",
  "Work.Agreement participant association": "taking part in a work agreement",
  "Work.Agreement pending leader": "being offered a work agreement's team",
  "Messaging.Forum subscriber roster": "the conversation's subscribers",
};

export const DERIVED_FORUM_NOTE_PREFIX = "Derived forum access:";

export function derivedForumSource(note: string): string {
  const source = note.slice(DERIVED_FORUM_NOTE_PREFIX.length).trim();
  return `${FROM} ${DERIVED_FORUM_SOURCES[source] ?? "a roster"}`;
}

/** The soft disable is called Remove everywhere: the switch, the menu, the filter and the row. */
export const REMOVE = "Remove";
export const SHOW_REMOVED = "Show removed";
export const GRANT = "Grant";
export const GRANT_AGAIN = "Grant again";

/**
 * Who granted or removed a grant, as a sentence names them: a person, the reader, or the system. A
 * person the directory cannot name is left out of the sentence rather than drawn as a blank.
 */
export type GrantActor = Readonly<{ type: "person"; name: string }> | Readonly<{ type: "viewer" }> | Readonly<{ type: "system" }>;

function byActor(actor: GrantActor): string {
  if (actor.type === "person") return `by ${actor.name}`;
  return actor.type === "viewer" ? "by you" : "by the system";
}

/**
 * The removed state of a grant: "Removed 30 Sep by Sam Ode", "Removed 30 Sep by the system", or
 * "Removed 30 Sep" while who removed it is unknown (#6825).
 */
export function removedOn(date: AHDate | undefined, today: AHDate, remover?: GrantActor): string {
  const removed = date ? `Removed ${grantDate(date, today)}` : "Removed";
  return remover ? `${removed} ${byActor(remover)}` : removed;
}

/** One earlier grant in a grant's history: "Granted 12 Aug by Sam Ode", or without "by" when unknown. */
export function grantedOnBy(date: AHDate | undefined, today: AHDate, grantor?: GrantActor): string {
  const granted = grantedOn(date, today);
  return grantor ? `${granted} ${byActor(grantor)}` : granted;
}

/** The details pane's history of a grant: the grants it replaced, each removed and given back (#6825). */
export const grantHistoryWording = {
  term: "Earlier grants",
  none: "None: this grant was never removed and given back.",
  count: (count: number) => (count === 1 ? "1 earlier grant, removed and given back" : `${count} earlier grants, removed and given back`),
  loading: "Loading the grant's history…",
  failed: "The grant's history could not be loaded.",
} as const;

/** Why a row cannot be changed, as a visible word and the sentence that explains it. */
export type GrantLockReason = "own" | "inherent" | "own-sign-in" | "platform" | "global" | "derived";

export type GrantLockWording = Readonly<{ word: string; explanation: string }>;

export function lockWording(reason: GrantLockReason, parent?: string): GrantLockWording {
  switch (reason) {
    case "own":
      return { word: "Locked", explanation: "You can't change your own access." };
    case "inherent":
      // Read/write access to your own notifications is decided by the server's authorization, not by a
      // grant, and the server refuses to disable it (#4438). Only its scope is the administrator's.
      return {
        word: "Always held",
        explanation:
          "Everyone keeps read/write access to their own notifications. Change the scope to give or withdraw entity-wide access.",
      };
    case "own-sign-in":
      return { word: "Locked", explanation: "Every member holds this so they can sign in. It isn't changed here." };
    case "platform":
      return { word: "Locked", explanation: "Platform-wide access is shown here but not changed here." };
    case "global":
      return { word: "Global", explanation: "Only a platform administrator can change a global grant." };
    case "derived":
      return { word: "Derived", explanation: `Set by ${parent ?? "another grant"}. Change it there.` };
  }
}

/** A removed row's state while the page that removed it is still open. */
export const REMOVED_JUST_NOW = "Removed by you just now";

/**
 * Under the kind on a standing row after the first, when the person holds the kind more than once:
 * the server deduplicates only an identical resource, so a second row is a second grant, not a copy.
 */
export const ALSO_GRANTED = "Also granted";

/** A row the apply template dialog will remove, before it is applied. */
export const REMOVED_WHEN_APPLIED = "Removed when applied";

/** Said on a row whose change the server refused because another administrator changed it first. */
export const CHANGED_BY_SOMEONE_ELSE = "Changed by someone else, reloaded";

/**
 * The CSV export on the person, record and kind pages (#6786): the button, its progress, its failure,
 * the file's column headings and the state column. Every other cell is written with the words a row
 * draws, from this module.
 */
export const grantExport = {
  button: "Export CSV",
  /** The button while the pages of grants are read: "Exporting… 200 of 587". */
  progress: (read: number, total: number | undefined): string =>
    total === undefined ? "Exporting…" : `Exporting… ${read.toLocaleString()} of ${total.toLocaleString()}`,
  /** What a failure says the person was doing: "Something went wrong exporting the grants". */
  doing: "exporting the grants",
  done: (count: number): string => `Exported ${count.toLocaleString()} grant${count === 1 ? "" : "s"}.`,
  columns: [
    "Person",
    "Email",
    "Area",
    "Kind",
    "Target type",
    "Target",
    "Access level",
    "Scope",
    "Fields",
    "State",
    "Source",
    "Granted",
    "Removed",
  ] as const,
  states: { enabled: "Granted", removed: "Removed" } as const,
};

function derivedGrants(count: number): string {
  return `${count} derived grant${count === 1 ? "" : "s"}`;
}

/**
 * What the page's live region says after each change, and what the removal's snackbar says. The kind
 * is the server's lower-case name, so the sentence reads "Removed proposals for Dana Ruiz".
 */
export const changeAnnouncements = {
  granted: (kind: string, person: string): string => `Granted ${kind} to ${person}`,
  grantedAgain: (kind: string, person: string, restored = 0): string =>
    restored > 0
      ? `Granted ${kind} to ${person} again and restored ${derivedGrants(restored)}`
      : `Granted ${kind} to ${person} again`,
  /**
   * A re-grant answered by a server from before #6189, which grants the removed grant again and
   * restores nothing it withdrew. Said instead of claiming the Undo undid everything.
   */
  grantedAgainWithoutRestoring: (kind: string, person: string, withdrawn: number): string =>
    `Granted ${kind} to ${person} again, but the ${derivedGrants(withdrawn)} removed with it did not come back. ` +
    "Grant them again from their own rows.",
  changed: (kind: string, person: string, level: string): string => `Changed ${kind} for ${person} to ${level}`,
  removed: (kind: string, person: string, withdrawn: number): string =>
    withdrawn > 0
      ? `Removed ${kind} for ${person} and ${derivedGrants(withdrawn)}`
      : `Removed ${kind} for ${person}`,
  removedAll: (person: string, disabled: number, withdrawn: number, loweredNotifications = false): string => {
    const notifications = "organization-wide notifications reduced to their own";
    if (disabled === 0 && withdrawn === 0) {
      return loweredNotifications ? `Removed all access for ${person}: ${notifications}` : `${person} held nothing to remove`;
    }
    const grants = `${disabled.toLocaleString()} grant${disabled === 1 ? "" : "s"}`;
    const removed = withdrawn > 0 ? `${grants} and ${derivedGrants(withdrawn)}` : grants;
    return loweredNotifications
      ? `Removed all access for ${person}: ${removed}, and ${notifications}`
      : `Removed all access for ${person}: ${removed}`;
  },
  restoredAll: (person: string): string => `Granted ${person}'s access again`,
};

/** "Remove all access…": the menu item, the review step and its typed confirmation (#6176). */
export const removeAllAccess = {
  menuItem: "Remove all access…",
  title: (person: string): string => `Remove all access for ${person}?`,
  intro: (person: string, organization: string | undefined): string =>
    `Every grant ${person} holds${organization ? ` in ${organization}` : ""} is removed in one step. The grants are kept as removed, so they can be granted again.`,
  standingHeading: "Standing grants",
  noStandingGrants: "No standing grants",
  recordsHeading: "Grants on single records",
  noRecordGrants: "None",
  /** Shown only when the person reads the whole organization's notifications. */
  notificationsReduced: (person: string): string => `${person}'s organization-wide notifications are reduced to their own.`,
  stopSignIn: "Also stop sign-in",
  stopSignInHelp: "Left clear, they can still sign in to Attorney Hub.",
  confirmLabel: (person: string): string => `Type ${person} to confirm`,
  /** What a person the directory does not name is confirmed with, in place of their name. */
  unnamedConfirmText: "remove all access",
  unnamedConfirmLabel: 'Type "remove all access" to confirm',
  confirm: "Remove all access",
};

function people(count: number): string {
  return `${count} ${count === 1 ? "person" : "people"}`;
}

/** How many people hold a kind, as a chip beside it: "3 people", or "None". */
export function holdersLabel(count: number): string {
  return count === 0 ? "None" : people(count);
}

/** The kind page's caption: "3 people hold signing authority". The kind is the server's lower-case name. */
export function holdersCaption(kind: string, count: number): string {
  if (count === 0) return `Nobody holds ${kind}`;
  return `${people(count)} ${count === 1 ? "holds" : "hold"} ${kind}`;
}

/** A per-record kind's count on the kind page: "12 documents". */
export function holderRecordCount(noun: string, count: number): string {
  return `${count} ${noun}${count === 1 ? "" : "s"}`;
}

/** What the kind page's bulk actions are called, and what each tells once it has run. */
export const REMOVE_ACCESS = "Remove access";
export const SET_LEVEL = "Set level";

export const bulkAnnouncements = {
  selected: (count: number): string => `${count} selected`,
  confirmRemoval: (kind: string, count: number): string => `Remove ${kind} for ${people(count)}?`,
  /** What the bulk removal's question says about undoing it. A re-grant restores the derived grants (#6189). */
  removalUndo: "Until you leave this page, each row keeps its switch to grant it again.",
  removed: (kind: string, done: number, asked: number): string =>
    done === asked ? `Removed ${kind} for ${people(done)}` : `Removed ${kind} for ${done} of ${people(asked)}`,
  changed: (kind: string, level: string, done: number, asked: number): string =>
    done === asked
      ? `Changed ${kind} to ${level} for ${people(done)}`
      : `Changed ${kind} to ${level} for ${done} of ${people(asked)}`,
};

/** A noun with its first letter raised, to start a sentence: "conversation" → "Conversation". */
function sentenceCase(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * Why a row cannot name its record, in the kind's own noun from the kinds endpoint, so a forum is a
 * "conversation" on every row. The reasons are the `ByIdsResult` ones, plus a lookup that failed.
 */
export const targetStates = {
  /** `missing`: the record names nothing any more. The row offers Remove. */
  missing: (noun: string): string => `${sentenceCase(noun)} no longer exists`,
  /** `unauthorized`: the record exists and the viewer may not read it. */
  unauthorized: (noun: string): string => `No access to this ${noun}`,
  /** The lookup itself failed. The row offers Retry. */
  failed: (noun: string): string => `Couldn't load this ${noun}`,
  /** Still being looked up, where the name is plain text and cannot be skeleton text. */
  loading: (noun: string): string => `Loading this ${noun}…`,
  /** What the support path shows for a record the viewer cannot name. */
  id: (noun: string, id: string): string => `Id of this ${noun}: ${id}`,
};

/** The details pane's terms for a per-record grant's record: "Conversation", "Conversation id". */
export const recordDetailTerms = {
  name: (noun: string): string => sentenceCase(noun),
  id: (noun: string): string => `${sentenceCase(noun)} id`,
};

/** The same three answers for a person, from the individuals lookup. */
export const personStates = {
  missing: "No longer a member",
  unauthorized: "A person you can't see",
  failed: "Couldn't load this person",
};

/** Where a person's name is plain text and cannot be skeleton text: a filter chip, a tooltip. */
export const LOADING_PERSON = "Loading this person…";

/**
 * How a sentence refers to a person it cannot name: "Granted proposals to a former member". A state
 * sentence ("No longer a member") is never put where a name goes. The person page refers to its own
 * subject as "this person", which every template there reads well with, possessives included.
 */
export const unnamedPerson = {
  missing: "a former member",
  unauthorized: "a person you can't see",
  /** Still loading, or the lookup failed. */
  unknown: "this person",
  /** The person a person page is about. */
  subject: "this person",
};

/**
 * One per-record kind's line on the person page: "12 proposal grants". The count is the server's
 * count of grants, not of records: a conversation with a direct grant and a derived one is two.
 */
export function recordCount(count: number, noun: string): string {
  return count === 0 ? "None" : `${count.toLocaleString()} ${noun} grant${count === 1 ? "" : "s"}`;
}

/** The same line under the Removed filter: "3 removed conversation grants". */
export function removedRecordCount(count: number, noun: string): string {
  return count === 0 ? "None" : `${count.toLocaleString()} removed ${noun} grant${count === 1 ? "" : "s"}`;
}

/**
 * Said when a name search finds no grant. The search answers only the newest of the matching records,
 * so the words say so rather than claiming the grant does not exist. A search of only the records the
 * viewer may open (`onlyReadable`) says that too.
 */
export function noSearchMatch(noun: string, query: string, limit: number, onlyReadable = false): string {
  if (onlyReadable)
    return (
      `No ${noun} grant matches "${query}" among the ${limit} newest ${noun}s you can open. ` +
      `A grant on a ${noun} you cannot open is still in the list without a search.`
    );
  return `No ${noun} grant matches "${query}" among the ${limit} newest ${noun}s with that name. A narrower search finds the rest.`;
}

/**
 * How many targets one grant holds when more than one grant opens the same record: "Direct + from
 * communications (2 grants)". The parts are each grant's source, in the order the row lists them.
 */
export function contributingGrants(sources: readonly string[]): string {
  return `${sources.join(" + ")} (${sources.length} grants)`;
}

/** A grant that names its record directly, as one part of {@link contributingGrants}. */
export const DIRECT = "Direct";

/**
 * The per-field chip on one proposal or one work agreement: "5 of 8 fields · 2 read/write". It opens
 * the field editor, which lists every field.
 */
export function fieldSummary(granted: number, total: number, writable: number): string {
  if (granted === 0) return `No fields of ${total}`;
  const fields = `${granted} of ${total} field${total === 1 ? "" : "s"}`;
  return writable > 0 ? `${fields} · ${writable} read/write` : fields;
}

/** The fields of a proposal or work agreement, as the field editor names them. */
export const FIELD_NAMES: Readonly<Record<WorkAgreementField, string>> = {
  [WorkAgreementField.ClientTeam]: "Client team",
  [WorkAgreementField.VendorTeam]: "Vendor team",
  [WorkAgreementField.Policies]: "Policies",
  [WorkAgreementField.Conflicts]: "Conflicts",
  [WorkAgreementField.FeeSchedule]: "Fee schedule",
  [WorkAgreementField.Currency]: "Currency",
  [WorkAgreementField.Discount]: "Discount",
  [WorkAgreementField.Invoices]: "Invoices",
};

/** The field editor's title: "Fields of one proposal for Dana Ruiz". */
export function fieldEditorTitle(kind: string, person: string): string {
  return `Fields of ${kind} for ${person}`;
}

/** What a field the grant does not name reads as in the field editor. */
export const FIELD_NOT_GRANTED = "Not granted";

/** The field editor's note on a derived field: the most it can be, and the grant that set it. */
export function fieldCeiling(level: string, parent: string): string {
  return `At most ${level.toLowerCase()}, set by ${parent}`;
}

/**
 * A document grant linked to a parent. Documents ignore the link when they decide who may open a
 * document, so the row says the link does nothing rather than drawing it as a source.
 */
export const LEGACY_LINK = "Not used for access";
export const LEGACY_LINK_EXPLANATION =
  "This grant was recorded as coming from another one. Documents no longer read that link when they " +
  "decide who may open a document, so it is listed here only so it can be removed.";

/** What the one-click Grant reads as for inherent notifications, which everybody already holds. */
export const WIDEN_TO_ORGANIZATION = "Widen to organization";

export const accessibleNames = {
  /** The row's switch. Its state is `aria-checked`, never part of the name. */
  switch: (kind: string, person: string): string => `${kind} for ${person}`,
  level: (control: "accessLevel" | "scope", kind: string, person: string, target?: string): string =>
    `${control === "accessLevel" ? "Access level" : "Scope"} of ${kind} for ${person}${target ? `, ${target}` : ""}`,
  grant: (kind: string, person: string): string => `Grant ${kind} to ${person}`,
  widen: (kind: string, person: string): string => `Widen ${kind} for ${person} to the whole organization`,
  actions: (kind: string, person: string): string => `Actions for ${kind}, ${person}`,
  grantAgain: (kind: string, person: string): string => `Grant ${kind} to ${person} again`,
  retryTarget: (noun: string, kind: string, person: string): string => `Retry loading ${noun} for ${kind}, ${person}`,
  /** The Retry of a grantor the directory failed to answer about. */
  retryGrantor: (kind: string, person: string): string => `Retry loading who granted ${kind} to ${person}`,
  loadingGrantor: (kind: string, person: string): string => `Loading who granted ${kind} to ${person}`,
  /** The Retry beside a person page's or a row's person whose lookup failed. */
  retryPerson: "Retry loading this person",
  removeTarget: (kind: string, person: string): string => `Remove ${kind} for ${person}`,
  targetId: (noun: string): string => `Id of this ${noun}`,
  showRecords: (noun: string, person: string): string => `Show ${person}'s ${noun} grants`,
  hideRecords: (noun: string, person: string): string => `Hide ${person}'s ${noun} grants`,
  fields: (kind: string, person: string, target?: string): string =>
    `Fields of ${kind} for ${person}${target ? `, ${target}` : ""}`,
  grants: (kind: string, person: string, target?: string): string =>
    `The grants behind ${kind} for ${person}${target ? `, ${target}` : ""}`,
};

/**
 * The record page (#6171): its caption, Check access, Add people and the Who has access link. The
 * noun is the kind's own from the kinds endpoint, so a forum is a "conversation" throughout.
 */
export const recordPage = {
  /** The record page's caption: what it lists, which is grant holders, never "people who can open it". */
  holdersCaption: (noun: string, count: number): string =>
    count === 0 ? `Nobody holds a grant on this ${noun}` : `${people(count)} ${count === 1 ? "holds" : "hold"} a grant on this ${noun}`,
  /**
   * The caption's second line. A conversation opens only through a grant on it, so the rows are every
   * way in: the ones written from communications or a roster are listed with where they came from.
   */
  everyWayIn: (noun: string): string =>
    `Every way into this ${noun} is a grant listed here, including those written from communications or a roster`,
  /**
   * A document's second line. Access to every document in the organization opens one with no grant
   * on it, so the rows are not every way in, and the line says so.
   */
  documentStandingAccess:
    "People with access to every document in the organization can open it too, with no grant listed here. Check access answers for one person.",
  /**
   * A proposal's or a work agreement's second line. The server writes a grant for a roster, a team, a
   * reviewer and access to every one in the organization, so those are rows; a person who left the
   * work keeps a read-only view with none.
   */
  workStandingAccess: (noun: string): string =>
    `Grants written from a roster, a team or access to every ${noun} in the organization are listed here. A person who left the work keeps a read-only view with no grant; Check access answers for one person.`,
  /**
   * The second line of a record that opens only through a grant on it: a calendar event, an
   * organization agreement, a fee schedule template or a team template.
   */
  onlyThroughGrants: (noun: string): string => `Every way into this ${noun} is a grant listed here`,
  /** The heading of a proposal's or work agreement's own conversations and documents, below its rows. */
  relatedRecords: (noun: string): string => `This ${noun}'s conversations and documents`,
  /** One group's summary: "Conversation: Proposal discussion · 2 people". */
  relatedRecord: (noun: string, name: string, count: number | undefined): string =>
    `${noun.charAt(0).toUpperCase()}${noun.slice(1)}: ${name}${count === undefined ? "" : ` · ${people(count)}`}`,
  relatedRecordsFailed: "Couldn't load this record's conversations and documents.",
  /** The page lists only the viewer's organization's holders, and says so. */
  ownOrganizationOnly: "Only people in your organization are listed.",
  openRecord: (noun: string): string => `Open the ${noun}`,
  /** When the record was opened, and by whom where its read says: "opened 2 Sep by Sam Ode". */
  opened: (date: string, by?: string): string => (by ? `opened ${date} by ${by}` : `opened ${date}`),
  /** The record's holders failed to load, in the words of table-empty-states.md. */
  holdersFailed: (noun: string): string => `Couldn't load holders of this ${noun}`,
  kindsFailed: "Couldn't load the kinds of permission",
  /**
   * The crumb for the screen a record page was reached from, named as that screen names itself:
   * "the documents" reads "Documents", so the crumb is announced "Back to Documents".
   */
  fromCrumb: (label: string): string => {
    const name = label.trim().replace(/^the\s+/i, "");
    return `${name.charAt(0).toUpperCase()}${name.slice(1)}`;
  },
  addPeople: "Add people",
  checkAccess: "Check access",
  checkAccessPrompt: (noun: string): string => `Can this person open the ${noun}?`,
  canOpen: (person: string, level: string): string => `${person} can open it · ${level}`,
  /** For a record whose answer names no level: a document, a proposal or a work agreement. */
  canOpenUnlevelled: (person: string): string => `${person} can open it`,
  /** A refusal the page does not know the name of. The reasons above it still reach the record. */
  withheld: "But something else on the record still keeps them out",
  cannotOpen: (person: string, noun: string): string => `${person} can't open this ${noun}`,
  /** A person who can open the record only as it was when they left the work it belongs to. */
  frozen: (date: string): string => `Only as it was on ${date}, when they left the work`,
  checkFailed: "Couldn't check access.",
  giveAccess: "Give access",
  findPerson: "Find a person",
  noMatch: (query: string): string => `Nobody here matches "${query}".`,
  addPeopleTitle: (noun: string, name: string): string => `Add people to the ${noun} ${name}`,
  addPeopleTitleUnnamed: (noun: string): string => `Add people to this ${noun}`,
  people: "People",
  level: "Level",
  grant: (count: number): string => (count === 0 ? "Grant" : `Grant to ${people(count)}`),
  added: (kind: string, done: number, asked: number): string =>
    done === asked ? `Granted ${kind} to ${people(done)}` : `Granted ${kind} to ${done} of ${people(asked)}`,
  /** What a partial failure says above the list of the people it failed for. */
  someFailed: (count: number): string => `${count === 1 ? "One person was" : `${count} people were`} not given access:`,
  retryFailed: "Retry for these",
  whoHasAccess: "Who has access",
  openFullPage: "Open full page",
  close: "Close",
};

/** Why a person can open a conversation, one line per grant that lets them, as Check access lists them. */
export const forumAccessReasons = {
  direct: (grantor: string | undefined): string => (grantor ? `Granted by ${grantor}` : "Granted directly"),
  parent: "From: their communications permission",
  roster: (source: string | undefined): string =>
    derivedForumSource(`${DERIVED_FORUM_NOTE_PREFIX}${source ?? ""}`),
  leftRoster: "Kept, read-only, after they left the work",
  system: "Given by the system when the work began",
  /** Not a grant: the person started the conversation, which lets them invite people into it (#7151). */
  creator: "They started this conversation",
  /** A rule this page does not know yet. It is still a grant the server counted. */
  other: "A grant this page does not recognize",
};

/**
 * Why a person can open a document, a proposal or a work agreement, one line per rule that lets them,
 * as Check access lists them. The noun is the record's.
 */
export const recordAccessReasons = {
  direct: forumAccessReasons.direct,
  system: "Given by the system",
  parent: "From: a standing permission they hold",
  /** On a proposal: the person leads it, is on a team on it, or reviews it. On a document: the same, for the proposal it belongs to. */
  derivedProposal: (noun: string): string =>
    noun === "proposal" ? "From: leading, teaming on or reviewing the proposal" : "From: taking part in the proposal it belongs to",
  roster: (noun: string): string =>
    noun === "work agreement" ? "From: the work agreement's roster" : "From: the roster of the work agreement it belongs to",
  entityWide: (noun: string): string => `From: access to every ${noun} in the organization`,
  leftRoster: forumAccessReasons.leftRoster,
  otherOrganization: "Through a grant they hold in another organization",
  other: forumAccessReasons.other,
  /** Why a document the reasons reach still does not open. */
  withheldByProposalField: "But their access to the proposal leaves out the field this document belongs to",
  /** Why a proposal the reasons reach still does not open. */
  withheldByUnsubmittedDraft: "But it's an unsubmitted draft, and they aren't on the side drafting it",
};

/** What Sign is on each record that has it (#7150): hiring a proposal, leading a work agreement, accepting an organization agreement. */
function signVerb(noun: string): string {
  if (noun === "proposal") return "hire";
  if (noun === "work agreement") return "lead";
  return "accept";
}

/**
 * Check access's actions beyond Open (#6830): the choice, the answer and why an action is refused.
 * The refusals are `RecordActionRefusals` on the server; the reasons above them reuse
 * `recordAccessReasons` and `forumAccessReasons`, so a rule reads the same on every page. Invite came
 * with #7151; on the organization it is answered on the person page, in `memberInvitationAnswer`.
 */
export const recordActions = {
  /** The choice's accessible name. */
  choice: "Action",
  /** The choice's options. On a conversation, Edit is posting in it. */
  name: (action: string, noun: string): string => {
    if (action === "edit") return noun === "conversation" ? "Post" : "Edit";
    if (action === "invite") return "Invite";
    if (action === "approve") return "Approve";
    if (action === "sign") return "Sign";
    return "Open";
  },
  /** The verb phrase the prompt and the answer use: "edit", "post in", "invite people to", "approve", "hire". */
  verb: (action: string, noun: string): string => {
    if (action === "edit") return noun === "conversation" ? "post in" : "edit";
    if (action === "invite") return "invite people to";
    if (action === "approve") return "approve";
    if (action === "sign") return signVerb(noun);
    return "open";
  },
  prompt: (verb: string, noun: string): string => `Can this person ${verb} the ${noun}?`,
  allowed: (person: string, verb: string): string => `${person} can ${verb} it`,
  refused: (person: string, verb: string, noun: string): string => `${person} can't ${verb} this ${noun}`,
  /** An allowed edit of a proposal, and the fields it still leaves out. */
  exceptFields: (fields: readonly string[]): string => `Except: ${fields.join(", ")}`,
  /** The heading over what lets a refused person open the record. */
  whatTheyHold: "What lets them open it:",
  /** A colleague an approval waits for, before their name has loaded. */
  unnamedColleague: "a colleague",
  /** The heading over the signing authority grants that let a person hire a proposal or lead a work agreement. */
  theirSigningAuthority: "Their signing authority:",
  /** Under an organization agreement's Sign: its acceptance asks the principal and Can edit, not signing authority. */
  acceptanceNeedsNoSigningAuthority: "Accepting an organization agreement asks for its principal at Can edit, not for signing authority",
  /** Under a signing authority refusal, as #6833 names it. */
  signingAuthorityWouldLetThem: "Signing authority would let them sign. It's given on their page.",
  refusals: {
    readOnly: "Everything that lets them in gives Can read only",
    workEnded: (noun: string): string =>
      noun === "work agreement"
        ? "The work has ended, so nobody takes it over as its leader"
        : `The work this ${noun} belongs to has ended, or they left it`,
    outsideAudience: "Reviewers read this thread and don't post in it",
    /** Invite's `outsideAudience`: any proposal thread, not only All Stakeholders, has an audience of its own. */
    outsideAudienceToInvite: "This proposal thread's audience leaves them out, so they can't bring anyone into it",
    notOnProposal: "They aren't the proposal's team leader, a reviewer or on a team on it",
    representsBothParties: "They hold a place on both sides of the proposal, and nobody acts for both",
    notEditingReviewer: "They review the proposal, and aren't one of its editing reviewers",
    teamMemberOnly: "They're on a team on the proposal. Only its team leader and editing reviewers change it",
    proposalClosed: "The proposal is archived, canceled or hired, so nobody changes it",
    noMemberInvitations: "They don't hold Member invitations for the whole organization",
    notConversationCreator: "Only the person who started this conversation invites people into it",
    notLeader: "Only the proposal's team leaders invite people onto its teams and reviewers",
    /**
     * The proposal's status waits for others' approval (#7153): the people of the viewer's organization,
     * by name, and another organization's, unnamed.
     */
    notAwaitingTheirApproval: (approvers: readonly string[], otherOrganization: boolean): string => {
      const awaited = otherOrganization ? [...approvers, "another organization"] : approvers;
      if (awaited.length === 0) return "The proposal isn't waiting for anyone's approval now";
      const list = awaited.length === 1 ? awaited[0] : `${awaited.slice(0, -1).join(", ")} and ${awaited.at(-1)}`;
      return `The proposal is waiting for approval from ${list}, not from them`;
    },
    notAssignedReviewer: "They're on a team on the proposal and aren't one of its reviewers. Only its reviewers approve it now",
    noSigningAuthority: "They hold no signing authority for the organization",
    signingAuthorityTooNarrow: "Their signing authority is narrower than hiring here asks",
    notClientLeader: "Only the proposal's client team leader hires it",
    workAlreadyAwarded: "Another bid for this work is already hired",
    notPrincipal: "Only the agreement's principal, the officer it names, accepts it",
    agreementNotOpen: "The agreement was already accepted or rejected",
    agreementIncomplete: "The agreement names no location for the organization yet, so nobody accepts it",
    /** A refusal this page does not know the name of. */
    other: "Something else on the record refuses it",
  },
};

/**
 * Invite on the organization, as the Member invitations row of the person page says it (#7151). The
 * organization has no record page, so its answer is a line under the kind that decides it.
 */
export const memberInvitationAnswer = {
  allowed: "Can invite people to the organization",
  readOnly: "Can't invite people: Member invitations is held at Can read only",
  noMemberInvitations: "Can't invite people: inviting needs Member invitations for the whole organization",
  /** A refusal this page does not know the name of. */
  other: "Can't invite people to the organization",
  failed: "Couldn't check whether they can invite people",
};

/**
 * What would open a record when Check access answers No (#6833), from the server's remedies. Only
 * what the viewer may see is named: a proposal field the viewer may not read is left out.
 */
export const recordAccessRemedies = {
  /**
   * Nothing reaches the record: "A grant on this document, or documents for the whole organization,
   * would open it." `standing` is a standing kind's name; `roster` adds the work's roster, which is
   * the leader's to change and so is named, not offered. `alsoField` is a document whose proposal
   * field the person's proposal access leaves out, which a document grant alone does not open.
   */
  nothingReaches: (noun: string, standing: string | undefined, roster: boolean, alsoField = false): string => {
    const grant = `a grant on this ${noun}`;
    if (roster) return `A place on its roster, or ${grant}, would open it. The work's leader changes the roster.`;
    const either = standing ? `${sentenceCase(grant)}, or ${standing} for the whole organization,` : sentenceCase(grant);
    return alsoField
      ? `${either} is needed, and so is a grant on the proposal that includes the field this ${noun} belongs to.`
      : `${either} would open it.`;
  },
  /**
   * A proposal field the viewer may read withholds the document: "Dana's access to the proposal "Q3"
   * leaves out Conflicts." `alone` when that field is all that keeps them out, so granting it opens it.
   */
  proposalField: (person: string, proposal: string | undefined, field: string, alone = true): string => {
    const leavesOut = `${person}'s access to the proposal${proposal ? ` "${proposal}"` : ""} leaves out ${field}.`;
    return alone ? `${leavesOut} A grant on the proposal that includes ${field} would open it.` : leavesOut;
  },
  /** The offer beside a named field: the proposal's page, where the person's grant and its fields are changed. */
  changeProposalAccess: "Change access to the proposal",
  /** No grant lifts a draft's refusal, so nothing is offered. */
  unsubmittedDraft: "No grant opens a draft before it is submitted.",
};
